import { CaretDownIcon, CheckIcon, WarningCircleIcon } from '@phosphor-icons/react';
import React from 'react';

import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useDialogContentContainer } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

export interface DestinationAccountOption {
  accountId: string;
  resourceId?: string;
  displayName?: string;
  identifier?: string;
  imageUrl: string | null;
}

export interface DestinationAccountPickerProps {
  /** Every currently-active connected account for this post's platform. */
  options: DestinationAccountOption[];
  /**
   * The stored override (`destination_connection_ids[platform]`), or
   * undefined when the post has never been targeted and simply resolves to
   * whichever single account exists.
   */
  value: string | undefined;
  resourceValue?: string | undefined;
  onChange: (accountId: string, resourceId?: string) => void;
  isSaving?: boolean;
  /** Disables the trigger entirely (post already published/publishing/failed). */
  disabled?: boolean;
  className?: string;
}

/**
 * Sibling of `PlacementPicker`/`AspectRatioPicker` — same trigger/menu
 * anatomy, so the toolbar row under the asset reads as one control family.
 *
 * Renders `null` whenever there is only one (or zero) active connected
 * account for the platform (Subtraction, rule 2 of the design system) — a
 * picker with nothing to pick is chrome, not an affordance. The moment the
 * database allows a second Instagram/Facebook/etc connection, this earns its
 * place; until then the surface is byte-for-byte what it always was.
 *
 * A stored override that no longer matches any active connection (the
 * account was disconnected since) is NOT silently dropped — it renders as an
 * explicit "no longer connected" row so the picker explains, in place, why
 * the publish path is about to refuse (`trg_social_posts_destination_connection_ownership`
 * plus the read-side honour check both fail closed on a stale id).
 */
export const DestinationAccountPicker: React.FC<DestinationAccountPickerProps> = ({
  options,
  value,
  resourceValue,
  onChange,
  isSaving,
  disabled,
  className,
}) => {
  const container = useDialogContentContainer();

  const selected = value
    ? options.find(
        (option) =>
          option.accountId === value &&
          (resourceValue === undefined || option.resourceId === resourceValue),
      )
    : undefined;
  const isStale = !!value && (!selected || (!!resourceValue && !selected.resourceId));

  // Nothing to choose between — hide entirely, including when the stored
  // override still happens to resolve (a single remaining account can't be
  // ambiguous). The stale case is the one exception: even with 0 or 1
  // options left, a dangling override must stay visible so its warning is
  // reachable, not silently disappear along with the rest of the control.
  if (options.length <= 1 && !isStale) return null;

  const isTriggerDisabled = !!disabled || !!isSaving;
  const triggerLabel = isStale
    ? 'Account disconnected'
    : (selected?.displayName ?? selected?.identifier ?? 'Choose account');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isTriggerDisabled}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'h-7 px-2 text-detail max-w-40',
            isStale && 'border-destructive/50 text-destructive',
            className,
          )}
        >
          {isSaving ? (
            <Spinner size="sm" />
          ) : isStale ? (
            <WarningCircleIcon className="size-3.5 shrink-0" weight="regular" />
          ) : (
            <Avatar
              size="xs"
              className="size-4"
              src={selected?.imageUrl}
              name={selected?.displayName ?? selected?.identifier}
            />
          )}
          <span className="truncate">{triggerLabel}</span>
          {!isTriggerDisabled ? <CaretDownIcon className="size-3 shrink-0" weight="bold" /> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" container={container}>
        <DropdownMenuLabel className="text-micro uppercase tracking-wider text-muted-foreground">
          Post as
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isStale ? (
          <div className="px-2 py-2 text-detail text-muted-foreground">
            The account this post was set to publish as is no longer connected. Choose another
            account below.
          </div>
        ) : null}
        {options.map((option) => {
          const isCurrent =
            !isStale &&
            option.accountId === value &&
            (resourceValue === undefined || option.resourceId === resourceValue);
          return (
            <DropdownMenuItem
              key={option.accountId}
              onSelect={() =>
                option.resourceId
                  ? onChange(option.accountId, option.resourceId)
                  : onChange(option.accountId)
              }
              className="flex items-center gap-2 py-2"
            >
              <span className="size-3.5 shrink-0 flex items-center justify-center">
                {isCurrent ? <CheckIcon className="size-3.5" weight="bold" /> : null}
              </span>
              <Avatar
                size="xs"
                className="shrink-0"
                src={option.imageUrl}
                name={option.displayName ?? option.identifier}
              />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium text-foreground">
                  {option.displayName ?? option.identifier ?? option.accountId}
                </span>
                {option.displayName && option.identifier ? (
                  <span className="truncate text-detail text-muted-foreground">
                    {option.identifier}
                  </span>
                ) : null}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
